import {
	CalendarOutlined,
	EnvironmentOutlined,
	FileTextOutlined,
	GlobalOutlined,
	HomeOutlined,
	IdcardOutlined,
	LinkOutlined,
	MailOutlined,
	MobileOutlined,
	PhoneOutlined,
	ShopOutlined,
	UserOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { addTPV, updateTPV } from "../../services/tpvService";

const TPVForm = ({ onTPVAdded, onTPVUpdated, tpvToEdit }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [isEditing, setIsEditing] = useState(false);

	// Cargar datos del TPV si estamos en modo edición
	useEffect(() => {
		if (tpvToEdit) {
			form.setFieldsValue({
				nombre: tpvToEdit.nombre,
				direccion: tpvToEdit.direccion,
				direccion2: tpvToEdit.direccion2,
				ciudad: tpvToEdit.ciudad,
				codigoPostal: tpvToEdit.codigoPostal,
				provincia: tpvToEdit.provincia,
				pais: tpvToEdit.pais,
				telefono: tpvToEdit.telefono,
				movil: tpvToEdit.movil,
				email: tpvToEdit.email,
				sitioWeb: tpvToEdit.sitioWeb,
				nifCif: tpvToEdit.nifCif,
				contacto: tpvToEdit.contacto,
				notas: tpvToEdit.notas,
				fechaAlta: tpvToEdit.fechaAlta ? dayjs(tpvToEdit.fechaAlta) : dayjs(),
				locationCode: tpvToEdit.locationCode,
				noSeries: tpvToEdit.noSeries,
				margenCierre: tpvToEdit.margenCierre,
			});
			setIsEditing(true);
		} else {
			form.resetFields();
			setIsEditing(false);
		}
	}, [tpvToEdit, form]);

	const onFinish = async (values) => {
		try {
			// Limpiar error previo
			setError("");
			setLoading(true);

			if (isEditing) {
				// Actualizar TPV existente
				const updatedTPV = await updateTPV(tpvToEdit.id, {
					nombre: values.nombre,
					direccion: values.direccion,
					direccion2: values.direccion2,
					ciudad: values.ciudad,
					codigoPostal: values.codigoPostal,
					provincia: values.provincia,
					pais: values.pais,
					telefono: values.telefono,
					movil: values.movil,
					email: values.email,
					sitioWeb: values.sitioWeb,
					nifCif: values.nifCif,
					contacto: values.contacto,
					notas: values.notas,
					fechaAlta: values.fechaAlta ? values.fechaAlta.toDate() : new Date(),
					locationCode: values.locationCode,
					noSeries: values.noSeries,
					margenCierre: values.margenCierre,
					pendingSync: 1,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Tienda actualizada correctamente");

				// Notificar al componente padre
				if (onTPVUpdated) onTPVUpdated(updatedTPV);
			} else {
				// Guardar TPV en IndexedDB
				const result = await addTPV({
					nombre: values.nombre,
					direccion: values.direccion,
					direccion2: values.direccion2,
					ciudad: values.ciudad,
					codigoPostal: values.codigoPostal,
					provincia: values.provincia,
					pais: values.pais,
					telefono: values.telefono,
					movil: values.movil,
					email: values.email,
					sitioWeb: values.sitioWeb,
					nifCif: values.nifCif,
					contacto: values.contacto,
					notas: values.notas,
					fechaAlta: values.fechaAlta ? values.fechaAlta.toDate() : new Date(),
					locationCode: values.locationCode,
					noSeries: values.noSeries,
					margenCierre: values.margenCierre,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Tienda registrada correctamente");

				// Limpiar formulario
				form.resetFields();

				// Notificar al componente padre
				if (onTPVAdded) onTPVAdded(result);
			}
		} catch (error) {
			// Mostrar error específico
			setError(error.message || "Error al procesar el TPV");
			console.error("Error al procesar TPV:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setIsEditing(false);
		if (onTPVUpdated) onTPVUpdated(null);
	};

	return (
		<div className="tpv-form-container">
			<h2>{isEditing ? "Editar Tienda" : "Registro de Tienda"}</h2>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Form
				form={form}
				name="tpv-form"
				onFinish={onFinish}
				layout="vertical"
				scrollToFirstError
			>
				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="nombre"
							label="Nombre"
							rules={[
								{
									required: true,
									message: "Por favor ingrese el nombre del TPV",
								},
							]}
						>
							<Input
								prefix={<ShopOutlined />}
								placeholder="Ingrese el nombre del TPV"
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item name="nifCif" label="Número de cédula/RUC/Passaporte">
							<Input prefix={<IdcardOutlined />} placeholder="Número de cédula/RUC/Passaporte" />
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Información de contacto</Divider>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item name="contacto" label="Persona de contacto">
							<Input
								prefix={<UserOutlined />}
								placeholder="Nombre de contacto"
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							name="email"
							label="Email"
							rules={[
								{
									type: "email",
									message: "El formato del email no es válido",
								},
							]}
						>
							<Input
								prefix={<MailOutlined />}
								placeholder="Correo electrónico"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={8}>
						<Form.Item name="telefono" label="Teléfono">
							<Input prefix={<PhoneOutlined />} placeholder="Teléfono fijo" />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item name="movil" label="N° celular">
							<Input prefix={<MobileOutlined />} placeholder="Número celular" />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item name="sitioWeb" label="Sitio Web">
							<Input prefix={<LinkOutlined />} placeholder="Sitio web" />
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Dirección</Divider>

				<Row gutter={16}>
					<Col xs={24}>
						<Form.Item name="direccion" label="Dirección">
							<Input
								prefix={<HomeOutlined />}
								placeholder="Dirección principal"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24}>
						<Form.Item name="direccion2" label="Dirección adicional">
							<Input
								prefix={<HomeOutlined />}
								placeholder="Dirección adicional (opcional)"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={8}>
						<Form.Item name="ciudad" label="Ciudad">
							<Input prefix={<EnvironmentOutlined />} placeholder="Ciudad" />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item name="codigoPostal" label="Código Postal">
							<Input placeholder="Código postal" />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item name="provincia" label="Provincia">
							<Input placeholder="Provincia" />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item name="pais" label="País">
							<Input prefix={<GlobalOutlined />} placeholder="País" />
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Información adicional</Divider>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item name="fechaAlta" label="Fecha de alta">
							<DatePicker
								style={{ width: "100%" }}
								placeholder="Seleccione fecha de alta"
								prefix={<CalendarOutlined />}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item name="locationCode" label="Código de ubicación">
							<Input placeholder="Código de ubicación" />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item name="noSeries" label="Serie de numeración">
							<Input placeholder="Serie de numeración" />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item name="margenCierre" label="Margen de cierre">
							<Input placeholder="Margen de cierre" />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24}>
						<Form.Item name="notas" label="Notas">
							<Input.TextArea
								rows={4}
								prefix={<FileTextOutlined />}
								placeholder="Notas adicionales"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item>
					<div style={{ display: "flex", gap: "10px" }}>
						<Button type="primary" htmlType="submit" loading={loading} block>
							{isEditing ? "Actualizar Tienda" : "Registrar Tienda"}
						</Button>

						{isEditing && (
							<Button onClick={handleCancel} block>
								Cancelar
							</Button>
						)}
					</div>
				</Form.Item>
			</Form>
		</div>
	);
};

export default TPVForm;
